"use client";
import React, { useState, useEffect } from "react";
import { NextPage } from "next";
import Head from "next/head";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaDownload,
} from "react-icons/fa";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { LoadingTable } from "../components/LoadingSpinner";
import { exportToCSVWithHeaders } from "@/lib/csvExport";

interface GenBankRow {
  GenBank_Accession: string;
  Strain_Isolate_Name: string | null;
  Sample_Collection_Date: string | null;
  Sample_Collection_Location: string | null;
  Sequence_Length: number | null;
  Isolate_Source: string | null;
  PubMed_ID: number | null;
  protein_count?: number;
}

interface ProteinCounts {
  [strainName: string]: number;
}

const BrowseProtein: NextPage = () => {
  const [aceessions, setGenBankAccession] = useState<GenBankRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortField, setSortField] = useState<keyof GenBankRow>("GenBank_Accession");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [strainSearch, setStrainSearch] = useState<string>("");
  const [locationFilter, setLocationFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("");
  const [allLocations, setAllLocations] = useState<string[]>([]);
  const [allDates, setAllDates] = useState<string[]>([]);
  const [proteinCounts, setProteinCounts] = useState<ProteinCounts>({});
  const [isLoading, setIsLoading] = useState(true);
  const searchParams = useSearchParams();
  const epitopeParam = searchParams.get("epitope");
  const [filteredGnomes, setFilteredGnomes] = useState<string[] | null>(null);

  useEffect(() => {
    const fetchEpitopeMapping = async () => {
      if (!epitopeParam) {
        setFilteredGnomes(null);
        return;
      }

      try {
        const res = await fetch(`/api/epitope-mapping/${epitopeParam}`);
        if (res.ok) {
          const data = await res.json();
          const gnomes = data.mapping.map((item: any) => item.gnome);
          setFilteredGnomes(gnomes);
        } else {
          console.error("Failed to fetch epitope mapping");
          setFilteredGnomes([]);
        }
      } catch (err) {
        console.error("Error fetching mapping:", err);
        setFilteredGnomes([]);
      }
    };
  
    fetchEpitopeMapping();
  }, [epitopeParam]);

  const fetchAntigen = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/gene-bank-data/", {
        method: "get",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: GenBankRow[] = await response.json();
        setGenBankAccession(data);

        const uniqueLocations = Array.from(
          new Set(data.map((row) => row.Sample_Collection_Location).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setAllLocations(uniqueLocations);

        const uniqueDates = Array.from(
          new Set(data.map((row) => row.Sample_Collection_Date).filter(Boolean))
        ).sort();
        setAllDates(uniqueDates);
      } else {
        console.error("Failed to fetch antigen list");
      }
    } catch (error) {
      console.error("Error fetching antigen:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProteinCounts = async () => {
    try {
      const response = await fetch("/api/genome-protein-counts");
      if (response.ok) {
        const counts: ProteinCounts = await response.json();
        setProteinCounts(counts);
      } else {
        console.error("Failed to fetch protein counts");
      }
    } catch (error) {
      console.error("Error fetching protein counts:", error);
    }
  };

  useEffect(() => {
    fetchAntigen();
    fetchProteinCounts();
  }, []);

  const handleSort = (field: keyof GenBankRow) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredAntigens = aceessions.filter((row) => {
    const matchesLocation = locationFilter === "" || row.Sample_Collection_Location === locationFilter;
    const matchesDate = dateFilter === "" || row.Sample_Collection_Date === dateFilter;
    const matchesStrain = strainSearch === "" || 
      (row.Strain_Isolate_Name && row.Strain_Isolate_Name.toLowerCase().includes(strainSearch.toLowerCase()));

    const matchesEpitope = !filteredGnomes || filteredGnomes.includes(row.GenBank_Accession);

    return matchesLocation && matchesStrain && matchesDate && matchesEpitope;
  });

  const sortedAntigens = [...filteredAntigens].sort((a, b) => {
    // Special handling for protein_count field
    if (sortField === "protein_count" as any) {
      const aCount = a.Strain_Isolate_Name ? (proteinCounts[a.Strain_Isolate_Name] || 0) : 0;
      const bCount = b.Strain_Isolate_Name ? (proteinCounts[b.Strain_Isolate_Name] || 0) : 0;
      return sortDirection === "asc" ? aCount - bCount : bCount - aCount;
    }
    
    const aVal = a[sortField];
    const bVal = b[sortField];
    
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sortDirection === "asc" ? 1 : -1;
    if (bVal == null) return sortDirection === "asc" ? -1 : 1;
    
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedAntigens.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredAntigens.length / recordsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [strainSearch, locationFilter, dateFilter]);

  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: keyof GenBankRow }) => {
    if (sortField !== field) return <FaSort className="inline ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  const handleExportCSV = () => {
    const csvData = currentRecords.map(item => ({
      "GenBank Accession": item.GenBank_Accession,
      "Strain/Isolate Name": item.Strain_Isolate_Name || "-",
      "Collection Date": item.Sample_Collection_Date || "-",
      "Collection Location": item.Sample_Collection_Location || "-",
      "Sequence Length": item.Sequence_Length ?? "-",
      "Isolate Source": item.Isolate_Source || "-",
      "PubMed ID": item.PubMed_ID || "-",
      "Protein Count": item.protein_count ?? 0,
    }));

    exportToCSVWithHeaders(
      csvData,
      `asfv-genome-${new Date().toISOString().split('T')[0]}`
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Head>
        <title>ASFV Genome - ASFV-EpiBase</title>
        <meta name="description" content="Browse ASFV genome data with genomic annotations and gene information in ASFV-EpiBase" />
      </Head>

      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">ASFV Genome</h1>
        <p className="text-gray-600">Browse ASFV genome data with genomic annotations and gene information</p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">

        <div className="flex flex-wrap items-center gap-4">
          {/* Strain Name Search */}
          <div className="flex items-center">
            <label className="mr-2">Search Strain Name:</label>
            <input
              type="text"
              placeholder="Enter strain name..."
              value={strainSearch}
              onChange={(e) => setStrainSearch(e.target.value)}
              className="border rounded px-3 py-1 w-48"
            />
          </div>

          {/* Location Filter */}
          <div className="flex items-center">
            <label className="mr-2">Filter by Location:</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="border rounded px-2 py-1"
              title="Filter by location"
              aria-label="Filter by location"
            >
              <option value="">All</option>
              {allLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          {/* Collection Date Filter */}
          <div className="flex items-center">
            <label className="mr-2">Filter by Collection Date:</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border rounded px-2 py-1"
              title="Filter by collection date"
              aria-label="Filter by collection date"
            >
              <option value="">All</option>
              {allDates.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          {/* Export CSV Button */}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm"
            title="Export to CSV"
          >
            <FaDownload className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        <div className="flex items-center">
          <span className="mr-2">Export Results</span>
          <button 
            className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
            title="Export Results"
            aria-label="Export Results"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="text-sm text-gray-700 mb-4 sm:flex-nowrap">
        {filteredAntigens.length} Records Found
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
        <div className="flex items-center mb-2 sm:mb-0">
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="mr-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="First page"
            aria-label="First page"
          >
            <FaAngleDoubleLeft />
          </button>
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="mr-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Previous page"
            aria-label="Previous page"
          >
            <FaAngleLeft />
          </button>
          <span className="mx-2">Page</span>
          <input
            type="text"
            className="border rounded px-2 py-1 w-12 sm:w-20 text-center"
            value={currentPage}
            title="Current page number"
            aria-label="Current page number"
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (!isNaN(page) && page > 0 && page <= totalPages) {
                setCurrentPage(page);
              }
            }}
          />
          <span className="mx-2">of {totalPages}</span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Next page"
            aria-label="Next page"
          >
            <FaAngleRight />
          </button>
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="ml-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Last page"
            aria-label="Last page"
          >
            <FaAngleDoubleRight />
          </button>
        </div>

        <div className="flex items-center">
          <select
            value={recordsPerPage}
            onChange={handleRecordsPerPageChange}
            className="border rounded px-2 py-1"
            title="Records per page"
            aria-label="Records per page"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="ml-2">records per page</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 text-left">
                No.
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("GenBank_Accession")}>
                Genome Accession (Link to NCBI) <SortIcon field="GenBank_Accession" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("Strain_Isolate_Name")}>
                Strain Name <SortIcon field="Strain_Isolate_Name" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("protein_count" as any)}>
                Number of Proteins <SortIcon field="protein_count" as any />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("Sample_Collection_Date")}>
                Collection Date <SortIcon field="Sample_Collection_Date" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("Sample_Collection_Location")}>
                Location <SortIcon field="Sample_Collection_Location" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("Sequence_Length")}>
                Length <SortIcon field="Sequence_Length" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("Isolate_Source")}>
                Isolation Source <SortIcon field="Isolate_Source" />
              </th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => handleSort("PubMed_ID")}>
                PubMed ID <SortIcon field="PubMed_ID" />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <LoadingTable message="Loading genome data..." />
            ) : currentRecords.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500 italic">
                  No data available.
                </td>
              </tr>
            ) : (
              currentRecords.map((row, index) => (
              <tr key={row.GenBank_Accession} className="border-b">
                <td className="px-4 py-2 text-gray-600">
                  {indexOfFirstRecord + index + 1}
                </td>
                <td className="px-4 py-2">
                  <Link
                    href={`https://www.ncbi.nlm.nih.gov/nuccore/${row.GenBank_Accession}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline hover:text-blue-600 transition"
                  >
                    {row.GenBank_Accession}
                  </Link>
                </td>
                <td className="px-4 py-2">
                  {row.Strain_Isolate_Name || "N/A"}
                </td>
                <td className="px-4 py-2">
                  {row.Strain_Isolate_Name && proteinCounts[row.Strain_Isolate_Name] ? (
                    <Link
                      href={`/protein?strain=${encodeURIComponent(row.Strain_Isolate_Name)}`}
                      className="text-blue-500 hover:underline hover:text-blue-600 transition font-semibold"
                    >
                      {proteinCounts[row.Strain_Isolate_Name].toLocaleString()}
                    </Link>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
                <td className="px-4 py-2">{row.Sample_Collection_Date || "N/A"}</td>
                <td className="px-4 py-2">{row.Sample_Collection_Location || "N/A"}</td>
                <td className="px-4 py-2">{row.Sequence_Length?.toLocaleString() || "N/A"}</td>
                <td className="px-4 py-2">{row.Isolate_Source || "N/A"}</td>
                <td className="px-4 py-2">
                  {row.PubMed_ID ? (
                    <Link
                      href={`https://pubmed.ncbi.nlm.nih.gov/${row.PubMed_ID}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline hover:text-blue-600 transition"
                    >
                      {row.PubMed_ID}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrowseProtein;